import momentParseFormat from 'moment-parseformat'

export const DEFAULT_DATE_DISPLAY_FORMAT = 'MM/DD/YYYY'

/** DATE, DATE TIME fields */
/*
 * Format date in MM/DD/YYYY style
 * If parameter is not present, current date is used
 *
 * @params date -
 *
 */
export const dateDisplayFormat = (dateValue = null) => {
  let year = '0'
  let month = '0'
  let day = '0'
  let dateStamp = null

  const expectedFormat = 'MM/DD/YYYY'

  if (!dateValue) dateStamp = new Date(Date.now())
  else {
    const detectedFormat = momentParseFormat(dateValue, { preferredOrder: 'MDY' })
    if (detectedFormat === 'YYYY-MM-DDTHH:mm:ss.SSSZ') return dateTimeDisplayFormat(dateValue)

    if (['number', 'string'].includes(typeof dateValue) && detectedFormat === expectedFormat) {
      dateStamp = new Date(dateValue)
    } else if (detectedFormat === 'YYYY-MM-DD') [year, month, day] = dateValue.split('-')
  }

  if (dateStamp) {
    day = dateStamp.getDate()
    month = dateStamp.getMonth() + 1
    year = dateStamp.getFullYear()
  }

  return `${month}/${day}/${year}`
}

/*
 * Format date in YYYY-MM-DD style
 * If parameter is not present, current date is used
 *
 * @params dateString - MM/DD/YYYY formatted date string
 *
 */
export const dateISOFormat = (dateString = null) => {
  let year = '0'
  let month = '0'
  let day = '0'

  if (dateString) {
    [month, day, year] = dateString.split('/')
    if (day.length === 1) day = `0${day}`

    if (month.length === 1) month = `0${month}`
  } else {
    const dateStamp = new Date(Date.now())
    day = dateStamp.getDate()
    if (day < 10) day = '0' + day

    month = dateStamp.getMonth() + 1
    if (month < 10) month = '0' + month

    year = dateStamp.getFullYear()
  }

  return `${year}-${month}-${day}`
}
/*
* Converts date + time string to MM/DD/YYYY HH:MM AM/PM format
*/
export const dateTimeDisplayFormat = (dateTimeString = null) => {
  if (!dateTimeString || (dateTimeString && !dateTimeString.includes('T'))) return ''
  const date = dateDisplayFormat(dateTimeString.split('T')[0])
  const time = timeDisplayFormat(dateTimeString.split('T')[1])

  return `${date} at ${time}`
}

/*
 * Format time in H:MM am/pm or HH:MM AM/PM style
 * If parameter is not present, current time is used
 *
 * @params timeString - 24HR HH:MM formatted time string
 * @params alternateFormat - format hours with 2 digits and capitalize AM/PM
 *
 */
export const timeDisplayFormat = (timeString = null, alternateFormat = false) => {
  let hours = '00'
  let minutes = '00'
  let ampm = 'am'

  if (timeString) {
    [hours, minutes] = timeString.split(':')
    hours = parseInt(hours)
  } else {
    const timestamp = new Date(Date.now())

    hours = timestamp.getHours()
    minutes = timestamp.getMinutes()

    if (minutes < 10) minutes = '0' + minutes
  }

  if (hours >= 12) {
    hours = hours === 12 ? hours : hours - 12
    ampm = 'pm'
  }

  if (alternateFormat) {
    if (hours < 10) hours = '0' + hours

    ampm = ampm.toUpperCase()
  }

  return `${hours}:${minutes} ${ampm}`
}

/*
 * Smart date helper (used in docket entry dialog)
 */
export const timeHelper = timeString => {
  const v = timeString
  const morningBegin = 7

  // Invalid
  if (v.match(/[0-9]{5,}/)) return false

  // Already formatted do nothing
  if (v.match(/([0-9]{1,2}):([0-9]{2})\s*([AP]M)/i)) return timeString

  const res = v.match(/([0-9]{1,2}):{0,1}([0-9]{1,2}){0,1}\s*([AP]M{0,1}){0,1}$/i)
  if (res === null) return

  let h = res[1]
  let m = res[2]
  let meridian = res[3]

  // User specified meridian
  if (meridian !== undefined) {
    meridian = meridian.toUpperCase()

    if (meridian === 'A') meridian = 'AM'

    if (meridian === 'P') meridian = 'PM'
  }

  // One or Two digit entry
  if (m === undefined) m = '00'

  // Three digit entry
  if (h.length === 2 && m.length === 1) {
    m = h.charAt(1) + m
    h = h.charAt(0)
  } else if (h.length === 2 && h > 23) {
    m = h.charAt(1)
    h = h.charAt(0)
  }

  // Invalid
  if (m > 59 || m < 0) return false

  // Invalid
  if (h > 23 || h < 0) return false

  // Military Time
  if (meridian === undefined) {
    if (h > 12) {
      if (h < 12) meridian = 'AM'
      else meridian = 'PM'

      h = (h - 12)
    } else if (h >= morningBegin && h < 12) meridian = 'AM'
    else meridian = 'PM'
  }

  // Set minutes to zero if not present
  if (m === undefined) m = '00'
  // Add trailing zero
  else if (m.length === 1) m = m + '0'

  if (h < 10) h = '0' + parseInt(h)

  return `${h}:${m} ${meridian}`
}

/*
 * Calculate age from birthdate to today
 */
export const calculatedAge = (date, uncertainDate = null) => {
  let ageEstimate = ''

  if (date !== null && date !== '') {
    let [birthYear, birthMonth, birthDay] = date.split('-')
    birthYear = parseInt(birthYear)
    birthMonth = parseInt(birthMonth)
    birthDay = parseInt(birthDay)

    const dateStamp = new Date(Date.now())
    const day = dateStamp.getDate()
    const month = dateStamp.getMonth() + 1
    const year = dateStamp.getFullYear()

    let yearEstimate = 0
    let monthEstimate = 0

    yearEstimate = year - birthYear
    // Haven't yet had this year's birthday
    if (month < birthMonth || (month === birthMonth && day < birthDay)) yearEstimate = yearEstimate - 1

    // show age in years if >= 1 year old
    if (yearEstimate >= 1) ageEstimate = yearEstimate + ' year' + (yearEstimate === 1 ? ' ' : 's ')
    else {
      // almost a year old
      if (month === birthMonth && day < birthDay) monthEstimate = 11
      // same year as birth
      else if (month >= birthMonth) monthEstimate = month - birthMonth
      // add 12 as in calendar year after birth
      else monthEstimate = month - birthMonth + 12

      if (monthEstimate < 1 || (monthEstimate === 1 && birthDay > day)) ageEstimate = '<1 month '
      else ageEstimate = monthEstimate + ' month' + (monthEstimate === 1 ? ' ' : 's ')
    }

    ageEstimate = (uncertainDate ? '~' : '') + ageEstimate + 'old'
  } else if (uncertainDate) ageEstimate = 'Unknown age'

  return ageEstimate
}

/**
 * Convert a string representing time from ISO format (HH:mm) to number of minutes since midnight
 *
 * @param {string} isoTime - Time string in ISO format (HH:mm)
 * @returns {number} Number of minutes
 */
export const convertISOTimeToMinutes = time => {
  if (!time) return -1

  const [hours, minutes] = time.split(':')
  return (parseInt(hours) || 0) * 60 + (parseInt(minutes) || 0)
}

/**
 * Convert a string representing time from ISO format (HH:mm) to US format (hh:mm a)
 *
 * @param {string} isoTime - Time string in ISO format (HH:mm)
 * @returns {string} Time string in US format (hh:mm a)
 */
export const convertISOTimeToUSTime = isoTime => {
  if (typeof isoTime !== 'string') return ''

  let [hours, minutes] = isoTime.split(':')
  let meridiem = 'AM'

  hours = parseInt(hours)
  minutes = parseInt(minutes)

  if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return ''

  if (hours > 11) {
    hours = hours - 12
    meridiem = 'PM'
  }

  if (hours === 0) hours = 12

  const usTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${meridiem}`

  return usTime
}

/**
 * Convert a number representing minutes since midnight to a Time string in ISO format (HH:mm)
 * @param {number} minutes - Number of minutes
 * @returns {string} Time string in ISO format (HH:mm)
 */
export const convertMinutesToISOTime = minutes => {
  if (isNaN(parseInt(minutes))) return ''

  const hours = Math.floor(minutes / 60)
  return `${hours.toString().padStart(2, '0')}:${(minutes - hours * 60).toString().padStart(2, '0')}`
}

/**
 * Convert a number representing minutes since midnight to a Time string in US format (hh:mm a)
 *
 * @param {number} minutes - Number of minutes
 * @returns {string} Time string in US format (HH:mm)
 */
export const convertMinutesToUSTime = minutes => {
  if (isNaN(parseInt(minutes))) return ''

  const isoTime = convertMinutesToISOTime(minutes)
  return convertISOTimeToUSTime(isoTime)
}

/**
 * Convert a string representing time from US format (hh:mm a) to ISO format (HH:mm)
 *
 * @param {string} usTime - Time string in US format (hh:mm a)
 * @returns {string} Time string in ISO format (HH:mm)
 */
export const convertUSTimeToISOTime = usTime => {
  if (!usTime) return ''

  let [hours, minutes, meridiem] = usTime.split(/[: ]/)

  hours = parseInt(hours)
  minutes = parseInt(minutes)

  if (isNaN(hours) || isNaN(minutes) || hours < 1 || hours > 12 || minutes < 0 || minutes > 59) return ''

  meridiem = (meridiem || '').toString().toUpperCase()
  if (!['AM', 'PM'].includes(meridiem)) return ''

  if (meridiem === 'AM' && hours === 12) hours = 0
  else if (meridiem === 'PM' && hours < 12) hours = hours + 12

  const isoTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`

  return isoTime
}

/**
 * Convert a string representing time from US format (hh:mm a) to number of minutes since midnight
 *
 * @param {string} usTime - Time string in US format (hh:mm a)
 * @returns {number} Number of minutes
 */
export const convertUSTimeToMinutes = usTime => {
  if (!usTime) return -1

  const isoTime = convertUSTimeToISOTime(usTime)
  return convertISOTimeToMinutes(isoTime)
}
